// Live Supabase fetchers for the portal.
// Each fetcher returns data shaped like src/data/content.js so pages can
// fall back to the static module when the live call errors or returns empty.

import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { toCompanySlug, brandingForSeriesName } from './meetings';

// ---------- Shared data cache ----------
// Every fetcher is wrapped in query(): concurrent callers share one request
// (the sidebar counts and the page no longer fetch the same rows twice),
// results are kept in memory, and a copy is saved to localStorage so a
// return visit renders instantly and the portal keeps working with no signal
// (e.g. in a hotel ballroom). Saved data is shown first, then refreshed.

const CACHE_PREFIX = 'goihes_cache_v2:';  // bump when cached data shape changes
const FRESH_MS = 60 * 1000;          // reuse without refetching for 1 minute
const memory = new Map();            // key -> { t, data } | { promise }

function readSaved(key) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function save(key, entry) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Storage full or blocked: in-memory cache still works for this visit.
  }
}

// Last known value for a key (memory first, then saved copy), or undefined.
function peek(key) {
  const mem = memory.get(key);
  if (mem && 'data' in mem) return mem;
  const saved = readSaved(key);
  if (saved) memory.set(key, saved);
  return saved || undefined;
}

// Wrap a loader so it's deduped and cached. The loader must THROW on a
// Supabase error (so an outage never overwrites good saved data) and return
// null/[] for "nothing there". The wrapped function never throws: on failure
// it falls back to the last known value, else null.
function query(name, loader) {
  const keyFor = (...args) => `${name}:${JSON.stringify(args)}`;
  const fetcher = async (...args) => {
    const key = keyFor(...args);
    const known = peek(key);
    if (known && Date.now() - known.t < FRESH_MS) return known.data;
    const inflight = memory.get(key);
    if (inflight && inflight.promise) return inflight.promise;
    const promise = loader(...args)
      .then((data) => {
        const entry = { t: Date.now(), data: data ?? null };
        memory.set(key, entry);
        save(key, entry);
        return entry.data;
      })
      .catch((e) => {
        logErr(name, e);
        if (known) {
          memory.set(key, known);
          return known.data;
        }
        memory.delete(key);
        return null;
      });
    memory.set(key, { ...(known || {}), promise });
    return promise;
  };
  fetcher.cacheKey = keyFor;
  return fetcher;
}

// Last known result of a fetcher for these args, or undefined if never loaded.
export function peekLive(fetcher, args) {
  if (!fetcher.cacheKey) return undefined;
  const known = peek(fetcher.cacheKey(...args));
  return known ? known.data : undefined;
}

// Hook: useLive(fetcher, args). Returns `undefined` while loading with
// nothing saved (pages show a skeleton), then the result — `null`/empty when
// there's nothing to show. Starts from the saved copy when there is one and
// refreshes in the background. Re-fetches when the device comes back online
// or the app returns to the foreground, so a flaky connection heals itself.
export function useLive(fetcher, args) {
  const [state, setState] = useState(() => ({ args, data: peekLive(fetcher, args) }));
  const [tick, setTick] = useState(0);
  const argsKey = JSON.stringify(args);

  useEffect(() => {
    const refresh = () => setTick((n) => n + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(argsKey);
    // New args (e.g. another meeting): never show the previous one's data.
    setState((prev) => (JSON.stringify(prev.args) === argsKey
      ? prev
      : { args: parsed, data: peekLive(fetcher, parsed) }));
    Promise.resolve()
      .then(() => fetcher(...parsed))
      .then((result) => { if (!cancelled) setState({ args: parsed, data: result ?? null }); })
      .catch(() => { if (!cancelled) setState({ args: parsed, data: null }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argsKey, tick]);

  return JSON.stringify(state.args) === argsKey ? state.data : peekLive(fetcher, args);
}

function logErr(context, error) {
  if (!error) return;
  // eslint-disable-next-line no-console
  console.warn(`[portal/live] ${context}:`, error.message || error);
}

export const listPortalEvents = query('listPortalEvents', async () => {
  const { data, error } = await supabase
    .from('events')
    .select('id, event_name, start_date, end_date, venue_name, venue_city, venue_state, portal_slug, series_id, meeting_series(name)')
    .eq('portal_visible', true)
    .order('start_date', { ascending: true });
  if (error) throw error;
  return data || [];
});

export const fetchEventBySlug = query('fetchEventBySlug', async (slug) => {
  if (!slug) return null;
  const { data, error } = await supabase
    .from('events')
    .select('*, meeting_series(id, name)')
    .eq('portal_slug', slug)
    .eq('portal_visible', true)
    .maybeSingle();
  if (error) throw error;
  return data || null;
});

// ---------- Upcoming meetings ----------
// Cross-council list of future portal-visible meetings (dates + locations)
// for the portal's Upcoming Meetings page. Pulls straight from events.

function formatMeetingDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start + 'T12:00:00');
  const md = { month: 'short', day: 'numeric' };
  if (!end || end === start) {
    return s.toLocaleDateString('en-US', { ...md, year: 'numeric' });
  }
  const e = new Date(end + 'T12:00:00');
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const startStr = s.toLocaleDateString('en-US', md);
  const endStr = sameMonth ? e.getDate() : e.toLocaleDateString('en-US', md);
  return `${startStr}–${endStr}, ${e.getFullYear()}`;
}

export const fetchUpcomingMeetings = query('fetchUpcomingMeetings', async (currentEventId) => {
  const today = new Date().toISOString().slice(0, 10);
  // Note: not filtered by portal_visible. Upcoming Meetings advertises all
  // future meetings across councils, even those whose own full portal isn't
  // built yet (portal_visible = false). Linking is gated on portal_visible
  // below so we never link to a portal that isn't ready.
  const { data, error } = await supabase
    .from('events')
    .select('id, event_name, start_date, end_date, venue_name, venue_city, venue_state, portal_slug, portal_visible, meeting_series(name)')
    .gte('start_date', today)
    .order('start_date', { ascending: true });
  if (error) throw error;
  if (!data) return [];
  return data.map((ev) => {
    const cityState = [ev.venue_city, ev.venue_state].filter(Boolean).join(', ');
    const location = [ev.venue_name, cityState].filter(Boolean).join(' · ');
    return {
      id: ev.id,
      slug: ev.portal_slug || null,
      // Only meetings with a live portal are tappable; others are info-only.
      hasPortal: !!ev.portal_visible && !!ev.portal_slug,
      council: ev.meeting_series?.name || '',
      // Normalized council key (ihes|cpes|csce|muia) for chip filtering,
      // resilient to name variants (e.g. MUAI vs MUIA).
      councilKey: brandingForSeriesName(ev.meeting_series?.name).slug,
      name: ev.event_name || '',
      dateLabel: formatMeetingDateRange(ev.start_date, ev.end_date),
      location,
      isCurrent: !!currentEventId && ev.id === currentEventId,
    };
  });
});

// ---------- Agenda ----------
// V1 merged `agenda_items` + `panels` by date/time. Reproduce that.

function toSessionFromAgendaItem(row, roomMap = {}) {
  return {
    time: row.start_time ? to12h(row.start_time) : '',
    end: row.end_time ? to12h(row.end_time) : '',
    title: row.title,
    type: normalizeItemType(row.item_type, row.title),
    // Location is either free text (location_custom) or a room assignment
    // (location_room_id → event_rooms), resolved via roomMap. Custom wins.
    loc: row.location_custom || roomMap[row.location_room_id] || undefined,
    // audience: 'executive' | 'supplier' | 'both'. Drives the "Executives
    // Only" badge on the portal so suppliers know a session isn't for them.
    audience: row.audience || undefined,
    _date: row.item_date,
    _startTime: row.start_time || '',  // raw HH:MM:SS 24h — for correct chronological sort
    _endTime: row.end_time || '',
  };
}

function toSessionFromPanel(row) {
  const titleRaw = row.panel_title || 'Company Panels';
  const isInsights = /\binsight/i.test(titleRaw);

  return {
    time: row.start_time ? to12h(row.start_time) : '',
    end: row.end_time ? to12h(row.end_time) : '',
    title: titleRaw,
    // Executive Insights panels stay separate from Company Panel grouping.
    type: isInsights ? 'insights' : 'panel',
    company: row.suppliers?.company_name || '',
    room: row.event_rooms?.room_name || '',
    // Non-grouped sessions (e.g. Executive Insights) render this as their
    // location line; grouped Company Panels drop it and show per-entry rooms.
    loc: row.event_rooms?.room_name || undefined,
    _date: row.panel_date,
    _startTime: row.start_time || '',
    _endTime: row.end_time || '',
    // Only real panel-schedule rows (one company per row) are grouped into
    // concurrent blocks; agenda items that merely look like panels are not.
    _fromPanels: true,
  };
}

// Collapse concurrent Company Panel sessions (same date + start + end) into
// a single grouped block with an `entries` array of { company, room }. Non-
// panel sessions pass through unchanged. Single-panel slots still use the
// grouped layout (1-entry list) for consistency.
function groupConcurrentPanels(sessions) {
  const out = [];
  const panelGroups = new Map();  // key: "date|start|end" → group ref

  for (const s of sessions) {
    if (s.type !== 'panel' || !s._fromPanels) {
      out.push(s);
      continue;
    }
    const key = `${s._date}|${s._startTime}|${s._endTime}`;
    const existing = panelGroups.get(key);
    if (existing) {
      existing.entries.push({ company: s.company, room: s.room });
    } else {
      const group = {
        time: s.time,
        end: s.end,
        // Keep Mission Control's name for the block (e.g. MUIA's "Company Labs").
        title: s.title || 'Company Panels',
        type: 'panel',
        entries: [{ company: s.company, room: s.room }],
        _date: s._date,
        _startTime: s._startTime,
        _endTime: s._endTime,
      };
      panelGroups.set(key, group);
      out.push(group);
    }
  }

  // Sort entries alphabetically by company name within each group.
  for (const group of panelGroups.values()) {
    group.entries.sort((a, b) => (a.company || '').localeCompare(b.company || ''));
  }

  return out;
}

// Mission Control stores item_type as one of: general | meal | break | reception | activity.
// Map those to the portal's canonical types (+ title-based inference when `general`).
function normalizeItemType(dbType, title = '') {
  const t = (dbType || '').toLowerCase();
  const lower = (title || '').toLowerCase();

  // Direct passthrough for values already in the canonical set.
  if (['meal', 'panel', 'keynote', 'insights', 'social', 'golf', 'break'].includes(t)) return t;

  // DB-specific mappings.
  if (t === 'reception') return 'social';
  if (t === 'activity') return /\bgolf\b/.test(lower) ? 'golf' : 'social';

  // `general` (or missing) → infer from title keywords.
  if (/\bgolf\b/.test(lower)) return 'golf';
  if (/\b(keynote|presentation|general session|speaker)\b/.test(lower)) return 'keynote';
  if (/\binsight/.test(lower)) return 'insights';
  // "panel"/"panels" only — not "Panelist" (e.g. "Hot Topics - Panelist TBD").
  if (/\bpanels?\b/.test(lower)) return 'panel';
  if (/\b(breakfast|lunch|dinner)\b/.test(lower)) return 'meal';
  if (/\b(reception|social|networking)\b/.test(lower)) return 'social';

  if (/\bbreak\b/.test(lower)) return 'break';

  return 'general';  // talks, Q&As, sessions — not a break
}

function to12h(t) {
  // expects "HH:MM:SS" or "HH:MM"; returns "H:MM AM/PM"
  if (!t) return '';
  const [hStr, m = '00'] = t.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function formatDayKey(isoDate) {
  // "2026-09-09" → "Wed, Sep 9"
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export const fetchAgenda = query('fetchAgenda', async (eventId) => {
  if (!eventId) return null;
  const [itemsRes, panelsRes, roomsRes] = await Promise.all([
    supabase
      .from('agenda_items')
      .select('id, title, item_date, start_time, end_time, item_type, location_custom, location_room_id, audience')
      .eq('event_id', eventId),
    supabase
      .from('panels')
      .select('id, panel_date, start_time, end_time, panel_title, suppliers(company_name), event_rooms(room_name)')
      .eq('event_id', eventId),
    supabase
      .from('event_rooms')
      .select('id, room_name')
      .eq('event_id', eventId),
  ]);
  if (itemsRes.error) throw itemsRes.error;
  if (panelsRes.error) throw panelsRes.error;
  if (roomsRes.error) throw roomsRes.error;

  // room id → name, so agenda items whose location is a room assignment
  // (location_room_id) show their room, not just free-text locations.
  const roomMap = {};
  (roomsRes.data || []).forEach((r) => { roomMap[r.id] = r.room_name; });

  const items = (itemsRes.data || []).map((row) => toSessionFromAgendaItem(row, roomMap));
  const panels = (panelsRes.data || []).map(toSessionFromPanel);
  const merged = [...items, ...panels];
  if (!merged.length) return null;

  // Group by day and sort by start time within each day
  const grouped = {};
  for (const s of merged) {
    const key = formatDayKey(s._date);
    if (!key) continue;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  }
  const ordered = Object.keys(grouped)
    .sort((a, b) => {
      const da = merged.find((s) => formatDayKey(s._date) === a)?._date || '';
      const db = merged.find((s) => formatDayKey(s._date) === b)?._date || '';
      return da.localeCompare(db);
    });
  const out = {};
  for (const key of ordered) {
    // Sort within a day by raw 24-hour HH:MM:SS start_time (string lex works
    // correctly at that format; "10:15 AM"-style comparison did not).
    const sorted = grouped[key].sort((a, b) => {
      if (a._startTime !== b._startTime) return a._startTime < b._startTime ? -1 : 1;
      return 0;
    });
    // Group concurrent Company Panels into single blocks with sub-entries.
    const groupedSessions = groupConcurrentPanels(sorted);
    // Keep the ISO date + raw 24h times (as date/start24/end24) so the
    // Agenda can open on today and mark what's happening now.
    out[key] = groupedSessions.map(({ _date, _startTime, _endTime, _fromPanels, ...rest }) => ({
      ...rest, date: _date, start24: _startTime, end24: _endTime,
    }));
  }
  return out;
});

// ---------- Attendees ----------
// V1 used RPC `get_portal_attendees` (SECURITY DEFINER) to bypass RLS.

export const fetchAttendees = query('fetchAttendees', async (eventId) => {
  if (!eventId) return null;
  const { data, error } = await supabase.rpc('get_portal_attendees', { p_event_id: eventId });
  if (error) throw error;
  if (!data || !data.length) return null;
  return data.map((ex) => {
    // V1 honored a `mailtohome` flag to pick home vs work address.
    const useHome = Boolean(ex.mailtohome);
    const street = useHome
      ? ex.home_address
      : (ex.address || ex.home_address || '');
    const city = useHome ? ex.home_city : (ex.city || ex.home_city || '');
    const state = useHome ? ex.home_state : (ex.state || ex.home_state || '');
    const zip = useHome ? ex.home_zip : (ex.zip || ex.home_zip || '');
    const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const addrLines = [street, cityLine].filter(Boolean);

    return {
      first: ex.first_name || ex.first || '',
      last: ex.last_name || ex.last || '',
      role: ex.title || ex.role || '',
      org: ex.organization || ex.org || '',
      // Office number only — never the mobile/cell (per request).
      phone: ex.phone || '',
      email: ex.email || '',
      addr: addrLines,
      legacy: ex.member_type === 'legacy',
    };
  });
});

// ---------- Supplier attendees (Corporate Members & Sponsors) ----------
// The Attendees directory lists people, not logos. Provider members come from
// get_portal_attendees (fetchAttendees); corporate members and sponsors come
// from supplier_registrations → registration_attendees here. Only registrations
// flagged show_on_portal appear, matching the Sponsors tab's visibility gate.
// Returns { corporateMembers: [...], sponsors: [...] } shaped for AttendeeRow.

export const fetchSupplierAttendees = query('fetchSupplierAttendees', async (eventId) => {
  if (!eventId) return null;
  const { data, error } = await supabase
    .from('supplier_registrations')
    .select(`
      company_name, membership_type, show_on_portal,
      suppliers(company_name),
      registration_attendees(first_name, last_name, title, phone, email, attendee_number)
    `)
    .eq('event_id', eventId)
    .eq('show_on_portal', true);
  if (error) throw error;
  if (!data || !data.length) return null;

  const corporateMembers = [];
  const sponsors = [];
  for (const reg of data) {
    const company = reg.suppliers?.company_name || reg.company_name || '';
    const people = (reg.registration_attendees || [])
      .slice()
      .sort((a, b) => (a.attendee_number ?? 0) - (b.attendee_number ?? 0) ||
        (a.last_name || '').localeCompare(b.last_name || ''))
      .map((a) => ({
        first: a.first_name || '',
        last: a.last_name || '',
        role: a.title || '',
        org: company,
        phone: a.phone || '',
        email: a.email || '',
      }));
    // membership_type is 'corporate_member' or 'sponsor'; default non-sponsors
    // to the corporate members bucket.
    const bucket = reg.membership_type === 'sponsor' ? sponsors : corporateMembers;
    bucket.push(...people);
  }

  // Directory order: alphabetical by last name, then first name — matches the
  // Provider Members list (get_portal_attendees ORDER BY last_name) so all
  // three tabs read A–Z by person and stay easy to scan.
  const byName = (a, b) =>
    (a.last || '').localeCompare(b.last || '') ||
    (a.first || '').localeCompare(b.first || '');
  corporateMembers.sort(byName);
  sponsors.sort(byName);

  if (!corporateMembers.length && !sponsors.length) return null;
  return { corporateMembers, sponsors };
});

// ---------- Guests ----------
// Non-executive, non-supplier attendees (speakers, staff, VIPs). Read via a
// SECURITY DEFINER RPC (get_portal_guests) that bypasses RLS but is gated to
// portal-visible events. Name / title / company / email only — no phone.

export const fetchGuests = query('fetchGuests', async (eventId) => {
  if (!eventId) return null;
  const { data, error } = await supabase.rpc('get_portal_guests', { p_event_id: eventId });
  if (error) throw error;
  if (!data || !data.length) return null;
  return data
    .map((g) => ({
      first: g.first_name || '',
      last: g.last_name || '',
      role: g.title || '',
      org: g.company_name || '',
      email: g.email || '',
      guest: true,
    }))
    .sort((a, b) =>
      (a.last || '').localeCompare(b.last || '') ||
      (a.first || '').localeCompare(b.first || ''));
});

// ---------- Hotel ----------
// Hotel data lives on the `events` row itself. Build the shape content.js uses.

export function hotelFromEvent(event) {
  if (!event) return null;
  const hasAny = event.venue_name || event.venue_address || event.hotel_website || event.checkin_time;
  if (!hasAny) return null;
  const address = [event.venue_address, [event.venue_city, event.venue_state].filter(Boolean).join(', '), event.venue_zip].filter(Boolean);

  // Room rate is stored as numeric `hotel_room_rate` in Mission Control
  // (an earlier build read a non-existent `hotel_rate`, so it always showed
  // [TBD]). Format the number as "$339/night".
  const rateNum = Number(event.hotel_room_rate);
  const rate = (event.hotel_room_rate !== null && event.hotel_room_rate !== '' && !Number.isNaN(rateNum))
    ? `$${rateNum}/night`
    : 'TBA';

  return {
    name: event.venue_name || '',
    // No hotel-tagline column yet; don't repeat the welcome message here.
    tagline: event.hotel_tagline || '',
    address: address.length ? address : [],
    // hotel_phone is the real column; venue_phone doesn't exist on events.
    phone: event.hotel_phone || event.venue_phone || '',
    checkIn: event.checkin_time || 'TBA',
    checkOut: event.checkout_time || 'TBA',
    rate,
    blockCloses: event.hotel_block_closes || 'TBA',
  };
}

// ---------- Sponsors ----------
// Flat list; IHES meetings don't use Platinum/Gold/Silver tiers.
// Returns alphabetical array of { name, logo, website, slug }.
//
// Sponsor detail page fetcher loads a single supplier_registrations row by
// event + matching company slug, plus the supplier's attendees from
// registration_attendees.

export const fetchSponsorDetail = query('fetchSponsorDetail', async (eventId, companySlug) => {
  if (!eventId || !companySlug) return null;
  const { data, error } = await supabase
    .from('supplier_registrations')
    .select(`
      id, portal_description,
      suppliers(id, company_name, logo_url, website, corporate_description),
      registration_attendees(first_name, last_name, title, phone, email, attendee_number)
    `)
    .eq('event_id', eventId)
    .eq('show_on_portal', true)
    // Sponsor detail pages are for sponsors only, not corporate members.
    .eq('membership_type', 'sponsor');
  if (error) throw error;
  if (!data || !data.length) return null;
  const match = data.find((r) => toCompanySlug(r.suppliers?.company_name) === companySlug);
  if (!match) return null;
  const attendees = (match.registration_attendees || [])
    .slice()
    .sort((a, b) => (a.attendee_number ?? 0) - (b.attendee_number ?? 0) ||
      (a.last_name || '').localeCompare(b.last_name || ''))
    .map((a) => ({
      first: a.first_name || '',
      last: a.last_name || '',
      title: a.title || '',
      phone: a.phone || '',
      email: a.email || '',
    }));
  return {
    name: match.suppliers?.company_name || '',
    logo: match.suppliers?.logo_url || null,
    website: match.suppliers?.website || null,
    attendees,
  };
});

export const fetchSponsors = query('fetchSponsors', async (eventId) => {
  if (!eventId) return null;
  const { data, error } = await supabase
    .from('supplier_registrations')
    .select('id, suppliers(company_name, logo_url, website)')
    .eq('event_id', eventId)
    .eq('show_on_portal', true)
    // Sponsors page shows sponsors only. Corporate members also carry
    // show_on_portal (for the Attendees directory), so filter by type here.
    .eq('membership_type', 'sponsor');
  if (error) throw error;              // outage → saved copy or empty state, never placeholders
  const sponsors = (data || [])
    .map((row) => ({
      name: row.suppliers?.company_name || '',
      logo: row.suppliers?.logo_url || null,
      website: row.suppliers?.website || null,
    }))
    .filter((s) => s.name)
    .sort((a, b) => a.name.localeCompare(b.name));
  return sponsors;                     // [] is a valid answer, not a fallback trigger
});

// ---------- Photos ----------
// portal_photos schema: { id, event_id, url, caption, sort_order, created_at }.
// Previous version selected non-existent thumbnail_url/category columns,
// which caused Supabase to reject the query and fall back to static tiles.

export const fetchPhotos = query('fetchPhotos', async (eventId) => {
  if (!eventId) return null;
  const { data, error } = await supabase
    .from('portal_photos')
    .select('id, url, caption, sort_order')
    .eq('event_id', eventId)
    .order('sort_order');
  if (error) throw error;
  if (!data || !data.length) return null;
  return data.map((p) => ({
    url: p.url,
    label: p.caption || '',
  }));
});

// ---------- Resources ----------

// Derive an uppercase type badge (PDF, PNG, DOCX…) from a file URL's extension.
function extFromUrl(url) {
  if (!url) return 'FILE';
  const clean = url.split('?')[0].split('#')[0];
  const m = clean.match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toUpperCase() : 'FILE';
}

export const fetchResources = query('fetchResources', async (eventId) => {
  if (!eventId) return null;
  // portal_resources has no file_size/file_type columns — selecting them
  // made the whole query error, so the page silently fell back to static
  // placeholders. Select only real columns and derive the type badge from
  // the file URL's extension.
  const { data, error } = await supabase
    .from('portal_resources')
    .select('id, title, description, file_url, sort_order')
    .eq('event_id', eventId)
    .order('sort_order');
  if (error) throw error;
  if (!data || !data.length) return null;
  return data.map((r) => ({
    title: r.title || 'Untitled',
    desc: r.description || '',
    size: '',
    ext: extFromUrl(r.file_url),
    url: r.file_url || null,
  }));
});

// ---------- FAQ ----------
// Source of truth is Mission Control's FAQ Library (`faq_library` — shared
// across events, keyed by series; a null series_id means "All Series").
// Event-specific overrides from `portal_faqs` are appended after the library
// rows so a series-wide answer can be tailored per event without duplication.

export const fetchFaqs = query('fetchFaqs', async (eventId, seriesId) => {
  const queries = [];
  if (seriesId) {
    queries.push(
      supabase
        .from('faq_library')
        .select('id, question, answer, sort_order, series_id')
        .or(`series_id.eq.${seriesId},series_id.is.null`)
        .order('sort_order')
    );
  }
  if (eventId) {
    queries.push(
      supabase
        .from('portal_faqs')
        .select('id, question, answer, sort_order')
        .eq('event_id', eventId)
        .order('sort_order')
    );
  }
  if (!queries.length) return null;
  const results = await Promise.all(queries);
  const all = results.flatMap((r) => {
    if (r.error) throw r.error;
    return r.data || [];
  });
  if (!all.length) return null;
  return all.map((r) => ({ q: r.question, a: r.answer }));
});

// ---------- Local guide ----------

export const fetchLocal = query('fetchLocal', async (eventId) => {
  if (!eventId) return null;
  // Columns are `address` and `website_url` — an earlier build selected a
  // non-existent `location` column, which errored the query and left the
  // page on static content.
  const { data, error } = await supabase
    .from('local_guide')
    .select('id, name, description, address, website_url, category, sort_order')
    .eq('event_id', eventId)
    .order('sort_order');
  if (error) throw error;
  if (!data || !data.length) return null;

  const dining = [];
  const transit = [];
  for (const r of data) {
    const cat = (r.category || 'dining').toLowerCase();
    if (cat === 'transit' || cat === 'transportation') {
      transit.push({ title: r.name, desc: r.description || '', url: r.website_url || '' });
    } else {
      dining.push({ name: r.name, desc: r.description || '', loc: r.address || '', url: r.website_url || '' });
    }
  }
  return { Dining: dining, transit };
});
